/*
  Cart drawer behaviour.

  Kept as a normal theme asset rather than a section `javascript` block: those
  are compiled into a shared bundle whose URL version does not rotate when the
  source changes, so edits can sit behind a stale CDN copy for a long time.
  A regular asset URL is versioned per file and updates immediately. It also
  means Liquid values must arrive through data attributes, which is why the
  fee handles and fee quantity are read off the drawer element.
*/
(() => {
  if (window.bjafCartDrawerLoaded) {
    return;
  }

  window.bjafCartDrawerLoaded = true;

  /* Fee lines are billed but never counted as items, mirroring the Liquid
     tally. The theme's own `cart-count` element listens for cart:change and
     writes the raw count, so the subtraction is patched into its setter - the
     one choke point every writer goes through - rather than racing it and
     letting the badge disagree with the drawer heading. */
  const drawerElement = document.querySelector('.cart-drawer');

  const feeHandles = (drawerElement?.dataset.bjafFeeHandles || '')
    .split(',')
    .filter(Boolean);

  let feeQuantity =
    Number.parseInt(drawerElement?.dataset.bjafFeeQuantity || '0', 10) || 0;

  const readFeeQuantity = (cart) => {
    if (!Array.isArray(cart?.items) || feeHandles.length === 0) {
      return feeQuantity;
    }

    return cart.items.reduce(
      (total, item) =>
        feeHandles.includes(item.handle) ? total + item.quantity : total,
      0
    );
  };

  /* cart:change bubbles up from documentElement, so this capture-phase
     listener always lands before any bubble-phase one, including the theme's. */
  document.addEventListener(
    'cart:change',
    (event) => {
      feeQuantity = readFeeQuantity(event.detail?.cart);
    },
    true
  );

  customElements.whenDefined('cart-count').then(() => {
    const prototype = customElements.get('cart-count').prototype;
    const descriptor = Object.getOwnPropertyDescriptor(prototype, 'itemCount');

    if (!descriptor?.set || prototype.bjafExcludesFees) {
      return;
    }

    Object.defineProperty(prototype, 'itemCount', {
      configurable: true,
      get: descriptor.get,
      set(count) {
        descriptor.set.call(this, Math.max(count - feeQuantity, 0));
      },
    });

    prototype.bjafExcludesFees = true;
  });

  /* The drawer header lives in the `header` slot, outside `.cart-drawer__inner`
     - the only region the theme's cart JS re-renders on `cart:change` - so the
     "N items" string has to be updated here or it goes stale (and would show
     e.g. "1 items" after a quantity change). */
  const syncCartHeaderCount = (cart) => {
    if (typeof cart?.item_count !== 'number') {
      return;
    }

    const productCount = Math.max(cart.item_count - readFeeQuantity(cart), 0);
    const countElement = document.querySelector(
      '.cart-drawer [data-bjaf-cart-count]'
    );

    if (countElement) {
      const suffix =
        productCount === 1
          ? countElement.dataset.labelOne
          : countElement.dataset.labelOther;

      countElement.textContent = `${productCount} ${suffix}`;
    }

    /* Impact's cart-count listener occasionally misses product-form additions
       from inside this re-rendering drawer. Drive the public header badge from
       the same cart payload so it never remains one item behind. The raw count
       goes in because the patched setter does the fee subtraction. */
    document.querySelectorAll('cart-count').forEach((cartCount) => {
      cartCount.itemCount = cart.item_count;
    });
  };

  /* Manus: at qty=min the minus glyph becomes trash. Liquid sets `is-remove`
     on first paint; keep it in sync if the input changes before a full
     section re-render lands. */
  const syncTrashMorph = (root = document) => {
    root.querySelectorAll('.cart-drawer .bjaf-line-item__decrease').forEach((button) => {
      const quantityInput = button
        .closest('quantity-selector')
        ?.querySelector('.quantity-input');
      const minimumQuantity = Number.parseInt(quantityInput?.min || '1', 10);
      const currentQuantity = Number.parseInt(quantityInput?.value || '1', 10);

      button.classList.toggle(
        'is-remove',
        currentQuantity <= minimumQuantity
      );
    });
  };

  /* Snappy cart drawer open/close (Mikkel: almost instant, short slide only).
     Also kill Impact's 1250ms delayed innerHTML swap on cart:change so line
     items update immediately instead of fading in late. */
  customElements.whenDefined('cart-drawer').then(() => {
    const CartDrawer = customElements.get('cart-drawer');
    if (!CartDrawer || CartDrawer.prototype._bjafSnappyPatched) {
      return;
    }

    CartDrawer.prototype._bjafSnappyPatched = true;

    const sectionIdFrom = (element) => {
      const section = element.closest('.shopify-section');
      return section ? section.id.replace('shopify-section-', '') : '';
    };

    CartDrawer.prototype._onCartChanged = async function (event) {
      const sectionHtml =
        event.detail.cart['sections'][sectionIdFrom(this)];
      if (!sectionHtml) {
        return;
      }

      const updatedDrawerContent = new DOMParser().parseFromString(
        sectionHtml,
        'text/html'
      );

      if (event.detail.cart['item_count'] > 0) {
        const currentInner = this.querySelector('.cart-drawer__inner');
        const updatedInner = updatedDrawerContent.querySelector(
          '.cart-drawer__inner'
        );
        if (!currentInner) {
          this.replaceChildren(
            document
              .createRange()
              .createContextualFragment(
                updatedDrawerContent.querySelector('.cart-drawer').innerHTML
              )
          );
        } else {
          currentInner.innerHTML = updatedInner.innerHTML;
          const footer = this.querySelector('[slot="footer"]');
          const updatedFooter = updatedDrawerContent.querySelector(
            '[slot="footer"]'
          );
          if (footer && updatedFooter) {
            footer.replaceChildren(...updatedFooter.childNodes);
          }
        }
      } else {
        this.replaceChildren(
          ...updatedDrawerContent.querySelector('.cart-drawer').childNodes
        );
      }
    };

    CartDrawer.prototype._showTransition = function (animate19 = true) {
      this._setInitialPosition();
      const content = this.shadowRoot?.querySelector('[part="content"]');
      const closeButton = this.shadowRoot?.querySelector(
        '[part="outside-close-button"]'
      );

      if (!content) {
        this.style.opacity = '1';
        this.style.visibility = 'visible';
        return Promise.resolve();
      }

      this.style.opacity = '1';
      this.style.visibility = 'visible';
      content.style.transition = animate19
        ? 'clip-path 0.16s cubic-bezier(0.22, 1, 0.36, 1)'
        : 'none';
      content.style.clipPath = this._getClipPathProperties()[0];
      Array.from(content.children).forEach((child) => {
        child.style.opacity = '1';
      });
      if (closeButton) {
        closeButton.style.opacity = '1';
      }

      void content.offsetWidth;
      content.style.clipPath = this._getClipPathProperties()[1];

      return new Promise((resolve) => {
        const done = () => {
          content.removeEventListener('transitionend', done);
          this.classList.add('show-close-cursor');
          resolve();
        };
        if (!animate19) {
          done();
          return;
        }
        content.addEventListener('transitionend', done);
        window.setTimeout(done, 220);
      });
    };

    CartDrawer.prototype._hideTransition = function () {
      const content = this.shadowRoot?.querySelector('[part="content"]');
      if (!content) {
        this.style.opacity = '0';
        this.style.visibility = 'hidden';
        return Promise.resolve();
      }

      content.style.transition = 'clip-path 0.14s cubic-bezier(0.22, 1, 0.36, 1)';
      content.style.clipPath = this._getClipPathProperties().reverse()[0];

      return new Promise((resolve) => {
        const done = () => {
          content.removeEventListener('transitionend', done);
          this.style.opacity = '0';
          this.style.visibility = 'hidden';
          this.classList.remove('show-close-cursor');
          resolve();
        };
        content.addEventListener('transitionend', done);
        window.setTimeout(done, 200);
      });
    };
  });

  document.addEventListener('dialog:after-show', (event) => {
    if (event.target.matches?.('.cart-drawer')) {
      // no decorative unlock animation
    }
  });

  /* Re-open drawer after /cart bounce (drawer-only mode). */
  document.addEventListener('DOMContentLoaded', () => {
    try {
      if (sessionStorage.getItem('bjaf-open-cart') !== '1') {
        return;
      }
      sessionStorage.removeItem('bjaf-open-cart');
    } catch (e) {
      return;
    }

    customElements.whenDefined('cart-drawer').then(() => {
      const drawer = document.querySelector('cart-drawer');
      drawer?.show?.(false);
    });
  });

  document.addEventListener('cart:change', (event) => {
    syncCartHeaderCount(event.detail?.cart);
    window.requestAnimationFrame(() => syncTrashMorph());
  });

  document.addEventListener('input', (event) => {
    if (!event.target.closest?.('.cart-drawer .quantity-input')) {
      return;
    }

    syncTrashMorph(event.target.closest('line-item-quantity') || document);
  });

  document.addEventListener(
    'click',
    (event) => {
      const decreaseButton = event.target.closest(
        '.cart-drawer [data-bjaf-remove-at-min]'
      );

      if (!decreaseButton) {
        return;
      }

      const quantityInput = decreaseButton
        .closest('quantity-selector')
        ?.querySelector('.quantity-input');
      const minimumQuantity = Number.parseInt(quantityInput?.min || '1', 10);
      const currentQuantity = Number.parseInt(quantityInput?.value || '1', 10);

      if (currentQuantity > minimumQuantity) {
        return;
      }

      const removeLink = decreaseButton
        .closest('line-item-quantity')
        ?.querySelector('[data-bjaf-remove-link]');

      if (!removeLink) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const refreshEmptyDrawer = (cartEvent) => {
        if (cartEvent.detail?.cart?.item_count !== 0) {
          return;
        }

        window.setTimeout(() => {
          if (document.querySelector('.cart-drawer line-item')) {
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          }
        }, 300);
      };

      document.addEventListener('cart:change', refreshEmptyDrawer, {
        once: true,
      });
      window.setTimeout(
        () => document.removeEventListener('cart:change', refreshEmptyDrawer),
        10000
      );
      removeLink.click();
    },
    true
  );

  // Manus `.bc-add-btn.on`: fill the outlined Add control while the product
  // is being added (row usually disappears after cart refresh).
  document.addEventListener(
    'click',
    (event) => {
      const addButton = event.target.closest(
        '.cart-drawer .bjaf-cart-deal__button'
      );
      if (!addButton || addButton.disabled) {
        return;
      }
      addButton.classList.add('on');
    },
    true
  );

  syncTrashMorph();
})();
